import { create } from 'zustand';

/**
 * Estado de sesión del usuario actual. Pure Zustand store — sin side
 * effects de almacenamiento. La persistencia en `TokenStorage` se aplica
 * desde fuera vía `authPersistence.ts`, suscribiéndose a este store. Esto
 * mantiene el store testable en aislamiento y desacopla del navegador.
 */
export interface AuthSession {
  token: string;
  /** Tipo del token, ej. `'Bearer'`. */
  tipoToken: string;
  /** Locale del usuario, según el backend. */
  locale: string;
  tenantNombre: string;
  usuarioNombre: string;
}

/** Nombres para mostrar. Vienen del login; el JWT no los trae. */
export interface Perfil {
  usuarioNombre: string | null;
  tenantNombre: string | null;
}

export interface AuthState {
  token: string | null;
  tipoToken: string | null;
  locale: string | null;
  tenantNombre: string | null;
  usuarioNombre: string | null;
  /** Setea sólo el token (usado por hidratación inicial desde TokenStorage). */
  setToken: (token: string | null) => void;
  /** Setea la sesión completa (post-login exitoso, post-tenant, post-2FA). */
  setSession: (session: AuthSession) => void;
  /**
   * Setea solo los nombres (hidratación desde storage tras recargar). El
   * JWT no los trae: si no se persisten, se pierden al recargar.
   */
  setPerfil: (perfil: Perfil) => void;
  /** Limpia toda la sesión local. Sinónimo de logout. */
  reset: () => void;
}

const initialState = {
  token: null,
  tipoToken: null,
  locale: null,
  tenantNombre: null,
  usuarioNombre: null,
} as const;

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,
  setToken: (token) => set({ token }),
  setSession: (session) =>
    set({
      token: session.token,
      tipoToken: session.tipoToken,
      locale: session.locale,
      tenantNombre: session.tenantNombre,
      usuarioNombre: session.usuarioNombre,
    }),
  setPerfil: (perfil) =>
    set({ usuarioNombre: perfil.usuarioNombre, tenantNombre: perfil.tenantNombre }),
  reset: () => set({ ...initialState }),
}));
